// services/highlightService.js
const API_BASE = `${process.env.REACT_APP_API_URL}/pdfs`;

// Generic authenticated request method (same as in commentService)
const makeAuthenticatedRequest = async (url, options = {}, retryCount = 0) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ No authentication token found');
      throw new Error('User not authenticated');
    }
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      ...options
    };
    
    const response = await fetch(url, defaultOptions);
    
    // Handle rate limit errors (429)
    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({}));
      const retryAfter = errorData.retryAfter || 60;
      
      const rateLimitError = new Error('Rate limit exceeded');
      rateLimitError.status = 429;
      rateLimitError.retryAfter = retryAfter;
      throw rateLimitError;
    }
    
    // Handle HTTP 401 with token refresh
    if (response.status === 401) {
      if (retryCount === 0) {
        const newToken = await refreshToken();
        if (newToken) {
          // Retry the request with new token
          return makeAuthenticatedRequest(url, options, retryCount + 1);
        }
      }
      
      // If refresh failed or already retried, logout
      handleLogout();
      throw new Error('Authentication failed. Please login again.');
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error (${response.status}): ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('❌ Authenticated request failed:', error);
    throw error;
  }
};

// Token refresh logic
let isRefreshing = false;
let failedQueue = [];

const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  
  try {
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });
    
    if (response.ok) {
      const data = await response.json();
      const newToken = data.accessToken;
      
      sessionStorage.setItem('token', newToken);
      
      // Process any queued requests
      failedQueue.forEach(promise => promise.resolve(newToken));
      failedQueue = [];
      
      return newToken;
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    console.error('❌ Token refresh failed:', error);
    
    failedQueue.forEach(promise => promise.reject(error));
    failedQueue = [];
    
    return null;
  } finally {
    isRefreshing = false;
  }
};

// Centralized logout handler
const handleLogout = () => {
  console.log('🔒 Authentication failed, logging out...');
  
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export const highlightService = {
  async getHighlights(title) {
    try {
      const url = `${API_BASE}/${title}/highlights`;
      
      const data = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return data.success ? data.highlights : [];
    } catch (error) {
      console.error('❌ Error fetching highlights:', error);
      
      if (error.status === 429) {
        return [];
      }
      
      if (error.message.includes('404')) {
        return [];
      }
      
      return [];
    }
  },

  async saveHighlights(title, highlights) {
    try {
      const url = `${API_BASE}/${title}/highlights`;

      const data = await makeAuthenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify({ highlights }),
      });
      
      return data.success;
    } catch (error) {
      console.error('❌ Error saving highlights:', error);
      
      if (error.status === 429) {
        return false;
      }
      
      if (error.message.includes('404')) {
        return false;
      }
      
      return false;
    }
  },

  async deleteHighlight(title, highlightId) {
    try {
      const url = `${API_BASE}/${title}/highlights/${highlightId}`;

      const data = await makeAuthenticatedRequest(url, {
        method: 'DELETE',
      });
      
      return data.success;
    } catch (error) {
      console.error('❌ Error deleting highlight:', error);
      
      if (error.status === 429) {
        return false;
      }
      
      if (error.message.includes('404')) {
        return false;
      }
      
      return false;
    }
  },

  async deleteMultipleHighlights(title, highlightIds) {
    try {
      const url = `${API_BASE}/${title}/highlights/bulk`;

      const data = await makeAuthenticatedRequest(url, {
        method: 'DELETE',
        body: JSON.stringify({ highlightIds }),
      });
      
      return data.success;
    } catch (error) {
      console.error('❌ Error deleting multiple highlights:', error);
      
      if (error.status === 429) {
        return false;
      }
      
      if (error.message.includes('404')) {
        return false;
      }
      
      return false;
    }
  }
};